import React, { createContext, useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient, { checkAuth, login as apiLogin, register as apiRegister, logout as apiLogout } from '../utils/apiClient';
import { toast } from 'sonner';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const navigate = useNavigate();

  // Check authentication status on mount
  useEffect(() => {
    verifyAuth();
  }, []);

  const verifyAuth = async () => {
    try {
      const token = localStorage.getItem('session_token');
      
      if (!token) {
        setLoading(false);
        return;
      }

      const userData = await checkAuth();
      
      if (userData) {
        setUser(userData);
        setIsAuthenticated(true);
        
        // Check onboarding status using apiClient
        try {
          const onboardingResponse = await apiClient.get('/user/onboarding-status');
          
          // If not completed onboarding and not on onboarding page, redirect
          if (!onboardingResponse.data.has_completed_onboarding && window.location.pathname !== '/onboarding') {
            setTimeout(() => navigate('/onboarding'), 100);
          }
        } catch (err) {
          console.error('Error checking onboarding:', err);
        }
      } else {
        // Token is invalid
        localStorage.removeItem('session_token');
        localStorage.removeItem('user');
        setUser(null);
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.error('Auth verification failed:', error);
      setUser(null);
      setIsAuthenticated(false);
      localStorage.removeItem('session_token');
      localStorage.removeItem('user');
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const data = await apiLogin(email, password);
      setUser(data.user);
      setIsAuthenticated(true);
      toast.success(`¡Bienvenido, ${data.user.name}!`);
      return data;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const register = async (name, email, password) => {
    try {
      const data = await apiRegister(name, email, password);
      setUser(data.user);
      setIsAuthenticated(true);
      toast.success(`¡Cuenta creada exitosamente! Bienvenido, ${data.user.name}!`);
      return data;
    } catch (error) {
      console.error('Register error:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await apiLogout();
      setUser(null);
      setIsAuthenticated(false);
      toast.success('Sesión cerrada exitosamente');
      navigate('/app/auth');
    } catch (error) {
      console.error('Logout error:', error);
      setUser(null);
      setIsAuthenticated(false);
      navigate('/app/auth');
    }
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    register,
    logout,
    verifyAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
